import { useLazyQuery } from '@apollo/client';
import { GET_TECHNICIAN_ROLES_FOR_DROPDOWN } from '@nile/banyan/src/Graphql/Mutations/role';
import { GET_AI_AGENT_DATA } from '@nile/banyan/src/Graphql/Query/ai';
import {
  AI_AGENTS_CONSTANT_FEATURE_ID_MAP,
  ALL_SHARD_MODULES,
  COMMON_CONSTANTS,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  Loader
} from '@nile/components';
import { AccountClient, useQuery } from '@nile/graphql';
import { GET_FIELD_FOR_DROPDOWN } from '@nile/graphql/src/Query/ticketfields';
import { SVGIcon } from '@superops/super-icons';
import {
  Badge,
  Button,
  Card,
  DisplayText,
  Dropdown,
  TextStyle
} from '@superops/ui-design-system';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { components } from 'react-select';
import RolePermission from '../Components/RolePermission';
import { UPDATE_AI_FEATURE } from '@nile/banyan/src/Graphql/Mutations/ai';

const BEST_PRACTICES = [
  {
    title: 'ai.triage.best.practices.title.1',
    description: 'ai.triage.best.practices.description.1'
  },
  {
    title: 'ai.triage.best.practices.title.2',
    description: 'ai.triage.best.practices.description.2'
  },
  {
    title: 'ai.triage.best.practices.title.3',
    description: 'ai.triage.best.practices.description.3'
  }
];

const CustomDropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <SVGIcon name="ArrowDown" color="white" />
  </components.DropdownIndicator>
);

const TriageConfiguration = (props) => {
  const { data, onUpdate, isOnboarding } = props;

  const featureId =
    data?.featureId || AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE;

  const [entityShare, setEntityShare] = useState(null);
  const [rolePermissionData, setRolePermissionData] = useState(null);

  const [roleOptions, setRoleOptions] = useState([]);

  const [selectedRole, setSelectedRole] = useState([]);

  const [statusOptions, setStatusOptions] = useState([]);

  const [afterSendingReplyStatus, setAfterSendingReplyStatus] = useState(null);

  const [afterReceivingReplyStatus, setAfterReceivingReplyStatus] = useState(
    null
  );

  const [isUpdating, setIsUpdating] = useState(false);

  const formatStatus = (str) => {
    return {
      name: str,
      label: str,
      value: str
    };
  };

  const { loading: agentDataLoading, refetch: refetchAgentData } = useQuery(
    GET_AI_AGENT_DATA,
    {
      client: AccountClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      variables: {
        input: {
          featureId
        }
      },
      onCompleted: (res) => {
        const response = res?.getAIFeature;
        const entityShareData = response?.entityShare;
        const agentData = response?.agentData;
        if (entityShareData) {
          setEntityShare(entityShare);
        }
        if (agentData?.afterSendingReply?.ticket?.status) {
          setAfterSendingReplyStatus(
            formatStatus(agentData?.afterSendingReply?.ticket?.status)
          );
        }
        if (agentData?.afterReceivingReply?.ticket?.status) {
          setAfterReceivingReplyStatus(
            formatStatus(agentData?.afterReceivingReply?.ticket?.status)
          );
        }
        if (agentData?.acceptFeedbackFrom?.roles?.length > 0) {
          const roles = agentData?.acceptFeedbackFrom?.roles?.map((role) => ({
            ...role,
            key: role.roleId,
            label: role.name,
            value: role.roleId
          }));
          setSelectedRole(roles);
        }
      }
    }
  );

  const onRoleSelectionChange = (item) => {
    setSelectedRole(item);
  };

  const onSendingReplyChange = (item) => {
    setAfterSendingReplyStatus(item);
  };

  const onReceivingReplyChange = (item) => {
    setAfterReceivingReplyStatus(item);
  };

  const [fetchRoleOptions, { loading: roleLoading }] = useLazyQuery(
    GET_TECHNICIAN_ROLES_FOR_DROPDOWN,
    {
      client: AccountClient,
      onCompleted: (res) => {
        const responseData = res?.getTechnicianRoleTypeRoles || [];
        if (responseData.length > 0) {
          const roleOptionsData = responseData.map((role) => {
            return {
              ...role,
              key: role.roleId,
              label: role.name,
              value: role.roleId
            };
          });
          setRoleOptions(roleOptionsData);
        }
      }
    }
  );

  const [fetchStatusOptions, { loading: statusLoading }] = useLazyQuery(
    GET_FIELD_FOR_DROPDOWN,
    {
      client: AccountClient,
      variables: {
        input: {
          entity: ALL_SHARD_MODULES.ticket,
          columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.STATUS
        }
      },
      onCompleted: (res) => {
        const responseData = res?.getField?.options || [];
        if (responseData.length > 0) {
          const statusOptionsData = [];
          responseData.forEach((status) => {
            if (
              status?.details?.constants !== 'CLOSED' ||
              status?.details?.constants !== 'RESOLVED'
            ) {
              statusOptionsData.push({
                ...status,
                key: status.value,
                label: status.value,
                value: status.value
              });
            }
          });
          setStatusOptions(statusOptionsData);
        }
      }
    }
  );

  const formatRoleIds = (roleArr) => {
    if (roleArr?.length) {
      return roleArr.map((role) => {
        return {
          roleId: role.roleId
        };
      });
    }
    return [];
  };

  const onSave = async () => {
    const afterSendingReply = {
      ticket: {
        status: afterSendingReplyStatus?.value
      }
    };

    const afterReceivingReply = {
      ticket: {
        status: afterReceivingReplyStatus?.value
      }
    };

    const acceptFeedbackFrom = {
      roles: selectedRole.map((role) => {
        return {
          roleId: role.roleId,
          name: role.name
        };
      })
    };

    let entityShareInput = {
      id: rolePermissionData?.id,
      roleShareType: rolePermissionData?.roleShareType
    };

    if (rolePermissionData?.roleShareType === 'SPECIFIC') {
      entityShareInput = {
        ...entityShareInput,
        addRoleIds: formatRoleIds(rolePermissionData?.addRoleIds) || [],
        deleteRoleIds: formatRoleIds(rolePermissionData?.deleteRoleIds) || []
      };
    }

    const input = {
      featureId: '14',
      agentData: {
        afterSendingReply,
        afterReceivingReply,
        acceptFeedbackFrom
      },
      updateEntityShare: entityShareInput
    };

    if (onUpdate) {
      try {
        setIsUpdating(true);
        const res = await onUpdate({
          input
        });
        if (res) {
          setIsUpdating(false);
          refetchAgentData();
        }
      } catch (error) {
        setIsUpdating(false);
      }
    }
  };

  const checkRolePermission = () => {
    if (rolePermissionData?.roleShareType === 'SPECIFIC') {
      const hasAddRoleIds = rolePermissionData?.addRoleIds?.length > 0;
      const hasDeleteRoleIds = rolePermissionData?.deleteRoleIds?.length > 0;
      return !(hasAddRoleIds || hasDeleteRoleIds);
    }
    return false;
  };

  const getIsSaveDisabled = () => {
    const isEmptyCheck =
      isEmpty(afterSendingReplyStatus) ||
      isEmpty(afterReceivingReplyStatus) ||
      isEmpty(selectedRole);
    if (isEmptyCheck) {
      return isEmptyCheck;
    }
    return checkRolePermission();
  };

  const isSaveDisabled = getIsSaveDisabled();

  if (agentDataLoading) {
    return (
      <div className="flex actr jctr w100 height300">
        <Loader />
      </div>
    );
  }

  if (isOnboarding) {
    const dropdownStyles = () => ({
      menu: (base) => ({
        ...base,
        background: 'var(--sops-ai-dark-bg-gradient)' // ✅ wrapped in quotes
      }),
      menuList: (base) => ({
        ...base,
        maxHeight: 200,
        overflowY: 'auto'
      }),
      option: (base) => ({
        ...base,
        background: 'transparent'
      }),
      container: (base) => ({
        ...base,
        background: '#2F3135',
        border: '1px solid #3D4144',
        borderRadius: '5px',
        paddingLeft: '5px'
      }),
      control: (base) => ({
        ...base,
        border: 'none !important'
      }),
      singleValue: (base) => ({
        ...base,
        color: 'white',
        fontWeight: '400'
      })
    });

    const roleDropDownStyles = () => ({
      menu: (base) => ({
        ...base,
        background: 'var(--sops-ai-dark-bg-gradient)'
      }),
      menuList: (base) => ({
        ...base,
        maxHeight: 200,
        overflowY: 'auto'
      }),
      option: (base) => ({
        ...base,
        background: 'transparent'
      }),
      control: (base) => ({
        ...base,
        border: 'none !important',
        borderBottom: '1px solid white !important'
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: '#252729'
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: 'white',
        fontWeight: '400'
      })
    });

    return (
      <div id="triage-onboarding-config">
        <div className="p10 br10 ai-dark-bg">
          <div className="mb20">
            <div className="mb10">
              <TextStyle>
                <FormattedMessage id="admin.aiMarketplace.triageConfiguration.question.requester" />
              </TextStyle>
            </div>
            <div className="w100 wm-200 ">
              <Dropdown
                options={statusOptions}
                onChange={onSendingReplyChange}
                value={afterSendingReplyStatus}
                defaultValue={afterSendingReplyStatus}
                isClearable={false}
                placeholder="Select status"
                // menuPortalTarget={document.body}
                menuPlacement="auto"
                noOptionsMessage={() => {
                  return statusLoading ? 'Loading...' : 'No options';
                }}
                onMenuOpen={!statusOptions.length && fetchStatusOptions}
                styles={dropdownStyles()}
                components={{
                  DropdownIndicator: CustomDropdownIndicator,
                  IndicatorSeparator: () => null
                }}
              />
            </div>
          </div>

          <div className="mb20">
            <div className="mb10">
              <TextStyle>
                <FormattedMessage id="admin.aiMarketplace.triageConfiguration.requester.response" />
              </TextStyle>
            </div>
            <div className="w100 wm-200">
              <Dropdown
                options={statusOptions}
                onChange={onReceivingReplyChange}
                value={afterReceivingReplyStatus}
                defaultValue={afterReceivingReplyStatus}
                isClearable={false}
                placeholder="Select status"
                // menuPortalTarget={document.body}
                menuPlacement="auto"
                noOptionsMessage={() => {
                  return statusLoading ? 'Loading...' : 'No options';
                }}
                onMenuOpen={!statusOptions?.length && fetchStatusOptions}
                styles={dropdownStyles()}
                components={{
                  DropdownIndicator: CustomDropdownIndicator,
                  IndicatorSeparator: () => null
                }}
              />
            </div>
          </div>

          <div className="mb20">
            <div className="mb10">
              <TextStyle>
                <FormattedMessage id="admin.aiMarketplace.agent.rolePermission.access.heading" />
              </TextStyle>
            </div>
            <RolePermission
              updateParent={setRolePermissionData}
              entityShare={entityShare}
              featureId="14"
              hideHeading
              isDarkTheme
              noTarget
              noBorder
              menuPlacement="top"
              dropdownStyles={roleDropDownStyles}
              dropdownComponents={{
                DropdownIndicator: CustomDropdownIndicator,
                IndicatorSeparator: () => null
              }}
            />
          </div>

          <div>
            <div className="mb10">
              <TextStyle>
                <FormattedMessage id="admin.aiMarketplace.triageConfiguration.agent.learn" />
              </TextStyle>
            </div>
            <div className="mb10">
              <Dropdown
                options={roleOptions}
                onChange={onRoleSelectionChange}
                value={selectedRole}
                defaultValue={selectedRole}
                isMulti
                isClearable={false}
                placeholder="Select"
                noBorder
                menuPlacement="top"
                styles={roleDropDownStyles()}
                components={{
                  DropdownIndicator: CustomDropdownIndicator,
                  IndicatorSeparator: () => null
                }}
                onMenuOpen={!roleOptions?.length && fetchRoleOptions}
              />
            </div>
          </div>
        </div>

        <div className="mt20 w100">
          <Button
            onClick={onSave}
            disabled={isSaveDisabled}
            aiPrimaryFill
            loading={isUpdating}
            fullWidth
          >
            <FormattedMessage id="Start Agent" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb80">
      <div className="flex jend">
        <Button
          onClick={onSave}
          disabled={isSaveDisabled}
          loading={isUpdating}
          primary
          icon={<SVGIcon name="Save" />}
        >
          <FormattedMessage id="common.save.agent" />
        </Button>
      </div>

      <div className="flex mb40">
        <div className="w100">
          <RolePermission
            updateParent={setRolePermissionData}
            entityShare={entityShare}
            featureId="14"
          />
        </div>
        <div className="w60 mt20">
          <Card noShadow padding="sizeSmall" gradientVariant="disabledLeft">
            <div className="flex actr g5">
              <div className="flex">
                <SVGIcon name="MonicaAIBg" size="xl" />
              </div>
              <div>
                <div className="flex g10 actr mb5">
                  <div>
                    <DisplayText element="p" size="large" weight="SemiBold">
                      <FormattedMessage id="ai.triage.agent.fully.autonomous" />
                    </DisplayText>
                  </div>
                  <div className="flex">
                    <Badge
                      size="large"
                      icon={<SVGIcon name="Lock" size="sm" />}
                      iconPosition="left"
                      status="info"
                    >
                      <FormattedMessage id="admin.integrations.card.comingsoon" />
                    </Badge>
                  </div>
                </div>
                <div className="mt5">
                  <DisplayText element="p" size="large" weight="Regular">
                    <FormattedMessage id="ai.triage.agent.fully.autonomous.description" />
                  </DisplayText>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="mb40" id="triage-treshold">
        <div className="f-16 mb10">
          <TextStyle weight="SemiBold">
            <FormattedMessage id="admin.aiMarketplace.triageConfiguration.feedback.heading" />
          </TextStyle>
        </div>
        <div className="mb20">
          <TextStyle variation="subdued">
            <FormattedMessage id="admin.aiMarketplace.triageConfiguration.feedback.description" />
          </TextStyle>
        </div>

        <div className="mb20">
          <div className="flex g20 ">
            <div className="flex g20 actr w100 mw-fc">
              <div className="w100">
                <FormattedMessage id="admin.aiMarketplace.triageConfiguration.choose.role" />
              </div>

              <div className="flex">
                <SVGIcon name="RightArrow" />
              </div>
            </div>

            <div id="role-dropdown" className="w100">
              <Dropdown
                options={roleOptions}
                onChange={onRoleSelectionChange}
                value={selectedRole}
                defaultValue={selectedRole}
                isMulti
                isClearable={false}
                placeholder="Select"
                menuPortalTarget={document.body}
                menuPlacement="auto"
                noOptionsMessage={() => {
                  return roleLoading ? 'Loading...' : 'No options';
                }}
                onMenuOpen={!roleOptions?.length && fetchRoleOptions}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mb40" id="triage-status">
        <TextStyle weight="SemiBold">
          <div className="f-16 mb20">
            <FormattedMessage id="admin.aiMarketplace.triageConfiguration.status.heading" />
          </div>
        </TextStyle>
        <div className="flex g20 mb20">
          <div className="flex g10 actr">
            <div className="flex">
              <SVGIcon name="HourglasswireEnd" />
            </div>
            <div className="flex actr">
              <TextStyle>
                <FormattedMessage id="admin.aiMarketplace.triageConfiguration.question.requester" />
              </TextStyle>
            </div>

            <div className="flex ml10">
              <SVGIcon name="RightArrow" />
            </div>
          </div>

          <div id="dropdown" className="w100 wm-300">
            <Dropdown
              options={statusOptions}
              onChange={onSendingReplyChange}
              value={afterSendingReplyStatus}
              defaultValue={afterSendingReplyStatus}
              isClearable={false}
              placeholder="Select status"
              menuPortalTarget={document.body}
              menuPlacement="auto"
              noOptionsMessage={() => {
                return statusLoading ? 'Loading...' : 'No options';
              }}
              onMenuOpen={!statusOptions.length && fetchStatusOptions}
            />
          </div>
        </div>

        <div className="flex g20 mt10">
          <div className="flex g10 actr">
            <div className="flex">
              <SVGIcon name="MonicaAIBg" />
            </div>
            <div className="flex actr">
              <TextStyle>
                <FormattedMessage id="admin.aiMarketplace.triageConfiguration.requester.response" />
              </TextStyle>
            </div>

            <div className="flex ml10">
              <SVGIcon name="RightArrow" />
            </div>
          </div>

          <div id="dropdown" className="w100 wm-300 ">
            <Dropdown
              options={statusOptions}
              onChange={onReceivingReplyChange}
              value={afterReceivingReplyStatus}
              defaultValue={afterReceivingReplyStatus}
              isClearable={false}
              placeholder="Select status"
              menuPortalTarget={document.body}
              menuPlacement="auto"
              noOptionsMessage={() => {
                return statusLoading ? 'Loading...' : 'No options';
              }}
              onMenuOpen={!statusOptions?.length && fetchStatusOptions}
            />
          </div>
        </div>
      </div>

      <div className="mt60" id="triage-agent-info">
        <div className="mb10">
          <DisplayText element="p" size="extraLarge" weight="SemiBold">
            <FormattedMessage id="ai.triage.agent.info.heading" />
          </DisplayText>
        </div>
        <TextStyle variation="subdued">
          <FormattedMessage id="ai.triage.agent.info.description" />
        </TextStyle>

        <div className="mt20">
          <div className="flex g20 actr">
            <div className="flex actr g3">
              <div>0%</div>
              <div className="ml3 mr3">
                <FormattedMessage id="label.to" />
              </div>
              <div>60%</div>
            </div>

            <div>
              <SVGIcon name="RightArrow" />
            </div>

            <div className="flex g3 actr">
              <div className="flex">
                <SVGIcon name="Conversation" />
              </div>
              <div className="ml3 mr3">
                <DisplayText element="p" size="large" weight="SemiBold">
                  <FormattedMessage id="ai.triage.agent.ask.question" />
                </DisplayText>
              </div>
            </div>
          </div>

          <div className="flex g20 actr mt15">
            <div className="flex actr g3">
              <div className="ml3 mr3">
                <FormattedMessage id="common.above" />
              </div>
              <div>60%</div>
            </div>

            <div>
              <SVGIcon name="RightArrow" />
            </div>

            <div className="flex g3 actr">
              <div className="flex">
                <SVGIcon name="AIWand" />
              </div>
              <div className="ml3 mr3">
                <DisplayText element="p" size="large" weight="SemiBold">
                  <FormattedMessage id="ai.triage.agent.field.mapping" />
                </DisplayText>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-60" id="triage-best-practices">
          <div className="mb10">
            <DisplayText element="p" size="extraLarge" weight="SemiBold">
              <FormattedMessage id="ai.triage.agent.best.practices.heading" />
            </DisplayText>
          </div>
          <TextStyle variation="subdued">
            <FormattedMessage id="ai.triage.agent.best.practices.description" />
          </TextStyle>
        </div>

        <div className="mt20" id="triage-best-practices-list">
          {BEST_PRACTICES.map((item, index) => (
            <div className="flex g10 mb20" key={index}>
              <div>
                <TextStyle>✅</TextStyle>
              </div>
              <div>
                <div className="mb5">
                  <DisplayText element="p" size="large" weight="SemiBold">
                    <FormattedMessage id={item.title} />
                  </DisplayText>
                </div>
                <DisplayText
                  element="p"
                  size="medium"
                  variant="subdued"
                  weight="Regular"
                >
                  <FormattedMessage id={item.description} />
                </DisplayText>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TriageConfiguration;
